import React, { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, AlertTriangle, TrendingUp, TrendingDown, Users, Building2, Filter, RefreshCw } from 'lucide-react';

export default function HistoricalAnalysis() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Master data for filters
  const [managers, setManagers] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [gyms, setGyms] = useState([]);
  
  // Filter state
  const [selectedManager, setSelectedManager] = useState('all');
  const [selectedTL, setSelectedTL] = useState('all');
  const [selectedGym, setSelectedGym] = useState('all');
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const today = new Date();
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 6);
    setToDate(today.toISOString().split('T')[0]);
    setFromDate(lastWeek.toISOString().split('T')[0]);
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      const [mgrsRes, tlsRes, gymsRes] = await Promise.all([
        axiosInstance.get('/managers'),
        axiosInstance.get('/team-leaders'),
        axiosInstance.get('/gyms'),
      ]);
      setManagers(mgrsRes.data);
      setTeamLeaders(tlsRes.data);
      setGyms(gymsRes.data);
    } catch (error) {
      console.error('Failed to load master data');
    }
  };

  // Cascading filter logic
  const filteredTLs = useMemo(() => {
    if (selectedManager === 'all') return teamLeaders;
    return teamLeaders.filter(tl => tl.manager_id === selectedManager);
  }, [selectedManager, teamLeaders]);

  const filteredGyms = useMemo(() => {
    let result = gyms;
    if (selectedManager !== 'all') {
      result = result.filter(g => g.manager_id === selectedManager);
    }
    if (selectedTL !== 'all') {
      result = result.filter(g => g.tl_id === selectedTL);
    }
    return result;
  }, [selectedManager, selectedTL, gyms]);

  // Handle manager change - reset downstream filters
  const handleManagerChange = (value) => {
    setSelectedManager(value);
    setSelectedTL('all');
    setSelectedGym('all');
  };

  // Handle TL change - reset gym filter
  const handleTLChange = (value) => {
    setSelectedTL(value);
    setSelectedGym('all');
  };

  const fetchData = async () => {
    if (!fromDate || !toDate) {
      toast.error('Please select date range');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/historical-analysis?from_date=${fromDate}&to_date=${toDate}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load historical data');
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on selections
  const filteredGymPerformance = useMemo(() => {
    if (!data?.gym_performance) return [];
    let result = data.gym_performance;
    
    if (selectedManager !== 'all') {
      const managerGyms = gyms.filter(g => g.manager_id === selectedManager).map(g => g.gym_id);
      result = result.filter(p => managerGyms.includes(p.gym_id));
    }
    if (selectedTL !== 'all') {
      const tlGyms = gyms.filter(g => g.tl_id === selectedTL).map(g => g.gym_id);
      result = result.filter(p => tlGyms.includes(p.gym_id));
    }
    if (selectedGym !== 'all') {
      result = result.filter(p => p.gym_id === selectedGym);
    }
    
    return result;
  }, [data, selectedManager, selectedTL, selectedGym, gyms]);

  const getStatusColor = (rate, target = 70) => {
    if (rate >= target) return '#10B981';
    if (rate >= target - 10) return '#F59E0B';
    return '#EF4444';
  };

  const getStatus = (rate, target = 60) => {
    if (rate >= target) return '✅';
    if (rate >= target - 10) return '🟡';
    return '🔴';
  };

  // Calculate dips
  const dips = useMemo(() => {
    if (!filteredGymPerformance) return [];
    const issues = [];
    
    filteredGymPerformance.forEach(gym => {
      if (gym.dialed === 0) {
        issues.push({
          gym: gym.gym, tl: gym.tl_name || '-', metric: 'No Data',
          expected: '>0', actual: '0', gap: '-100%', severity: '🔴',
          recurring: 'Check', action: 'Verify'
        });
      } else if (gym.connect_pct < 60) {
        issues.push({
          gym: gym.gym, tl: gym.tl_name || '-', metric: 'Connect Rate',
          expected: '70%', actual: `${gym.connect_pct}%`, gap: `${gym.connect_pct - 70}%`,
          severity: '🔴', recurring: gym.connect_pct < 55 ? 'Yes' : 'No',
          action: gym.connect_pct < 55 ? 'Escalate' : 'Review'
        });
      } else if (gym.connect_pct < 70) {
        issues.push({
          gym: gym.gym, tl: gym.tl_name || '-', metric: 'Connect Rate',
          expected: '70%', actual: `${gym.connect_pct}%`, gap: `${gym.connect_pct - 70}%`,
          severity: '🟡', recurring: 'Monitor', action: 'Review'
        });
      }
    });
    
    issues.sort((a, b) => (a.severity === '🔴' ? -1 : 1) - (b.severity === '🔴' ? -1 : 1));
    return issues;
  }, [filteredGymPerformance]);

  // Summary stats
  const summaryStats = useMemo(() => {
    if (!data?.daily_trend) return { days: 0, dialed: 0, connected: 0, closures: 0, connectPct: 0 };
    const totals = data.daily_trend.reduce((acc, d) => ({
      dialed: acc.dialed + d.dialed,
      connected: acc.connected + d.connected,
      closures: acc.closures + d.closures
    }), { dialed: 0, connected: 0, closures: 0 });
    
    return {
      days: data.daily_trend.length,
      ...totals,
      connectPct: totals.dialed > 0 ? Math.round(totals.connected / totals.dialed * 100) : 0
    };
  }, [data]);

  return (
    <div data-testid="historical-analysis-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-800">
            📈 HISTORICAL ANALYSIS
          </h1>
          <p className="text-sm text-slate-500 mt-1">Deep dive into past | Find patterns | Identify issues</p>
        </div>
      </div>

      {/* Filters Card */}
      <div className="modern-card">
        <div className="modern-card-header">
          <div className="flex items-center gap-2 text-slate-700 font-semibold">
            <Filter size={18} />
            <span>Filters & Date Range</span>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Date Range */}
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-10"
                data-testid="from-date"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-10"
                data-testid="to-date"
              />
            </div>
            
            {/* Cascading Filters */}
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Manager</Label>
              <Select value={selectedManager} onValueChange={handleManagerChange}>
                <SelectTrigger className="h-10" data-testid="manager-filter">
                  <SelectValue placeholder="All Managers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Managers</SelectItem>
                  {managers.map(m => (
                    <SelectItem key={m.manager_id} value={m.manager_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Team Leader</Label>
              <Select value={selectedTL} onValueChange={handleTLChange}>
                <SelectTrigger className="h-10" data-testid="tl-filter">
                  <SelectValue placeholder="All TLs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All TLs</SelectItem>
                  {filteredTLs.map(tl => (
                    <SelectItem key={tl.tl_id} value={tl.tl_id}>{tl.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Gym</Label>
              <Select value={selectedGym} onValueChange={setSelectedGym}>
                <SelectTrigger className="h-10" data-testid="gym-filter">
                  <SelectValue placeholder="All Gyms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Gyms</SelectItem>
                  {filteredGyms.map(g => (
                    <SelectItem key={g.gym_id} value={g.gym_id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={fetchData} 
                disabled={loading} 
                className="w-full h-10 bg-[#1F4E79] hover:bg-[#163A5C] transition-all"
                data-testid="analyze-btn"
              >
                {loading ? (
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                ) : (
                  <Search size={16} className="mr-2" />
                )}
                Analyze
              </Button>
            </div>
          </div>
          
          {/* Active Filters Display */}
          {(selectedManager !== 'all' || selectedTL !== 'all' || selectedGym !== 'all') && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedManager !== 'all' && (
                <span className="status-badge info">
                  Manager: {managers.find(m => m.manager_id === selectedManager)?.name}
                </span>
              )}
              {selectedTL !== 'all' && (
                <span className="status-badge info">
                  TL: {teamLeaders.find(t => t.tl_id === selectedTL)?.name}
                </span>
              )}
              {selectedGym !== 'all' && (
                <span className="status-badge info">
                  Gym: {gyms.find(g => g.gym_id === selectedGym)?.name}
                </span>
              )}
              <button 
                onClick={() => { setSelectedManager('all'); setSelectedTL('all'); setSelectedGym('all'); }}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {data && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="metric-card">
              <div className="metric-label">Days Analyzed</div>
              <div className="metric-value text-[#1F4E79]">{summaryStats.days}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Total Dialed</div>
              <div className="metric-value text-slate-800">{summaryStats.dialed.toLocaleString()}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Total Connected</div>
              <div className="metric-value text-slate-800">{summaryStats.connected.toLocaleString()}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Avg Connect %</div>
              <div className="metric-value" style={{ color: getStatusColor(summaryStats.connectPct) }}>
                {summaryStats.connectPct}%
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Total Closures</div>
              <div className="metric-value text-emerald-600">{summaryStats.closures.toLocaleString()}</div>
            </div>
            <div className="metric-card border-red-200 bg-red-50/50">
              <div className="metric-label text-red-600">Issues Found</div>
              <div className="metric-value text-red-600">{dips.length}</div>
            </div>
          </div>

          {/* Daily Trend */}
          <div className="modern-card">
            <div className="modern-card-header">
              <h3 className="section-header">
                <TrendingUp size={20} className="text-[#1F4E79]" />
                Daily Trend
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="text-center">Dialed</th>
                    <th className="text-center">Connected</th>
                    <th className="text-center">Connect %</th>
                    <th className="text-center">Visit Sched</th>
                    <th className="text-center">Visit Done</th>
                    <th className="text-center">Close Exp</th>
                    <th className="text-center">Close Done</th>
                    <th className="text-center">Closures</th>
                    <th className="text-center">Rate</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily_trend?.map((day, idx) => {
                    const connectPct = day.dialed > 0 ? Math.round((day.connected / day.dialed) * 100) : 0;
                    const closureRate = day.closure_expected > 0 ? Math.round((day.closures / day.closure_expected) * 100) : 0;
                    return (
                      <tr key={idx}>
                        <td className="font-semibold text-slate-800">{day.date}</td>
                        <td className="text-center">{day.dialed}</td>
                        <td className="text-center">{day.connected}</td>
                        <td className="text-center">
                          <span className="font-semibold" style={{ color: getStatusColor(connectPct) }}>
                            {connectPct}%
                          </span>
                        </td>
                        <td className="text-center">{day.visit_scheduled || 0}</td>
                        <td className="text-center">{day.visit_done || 0}</td>
                        <td className="text-center text-purple-600">{day.closure_expected || 0}</td>
                        <td className="text-center text-emerald-600">{day.closure_done || 0}</td>
                        <td className="text-center font-semibold text-emerald-600">{day.closures}</td>
                        <td className="text-center">
                          <span className="font-medium" style={{ color: getStatusColor(closureRate, 60) }}>
                            {closureRate}%
                          </span>
                        </td>
                        <td className="text-center text-xl">{getStatus(closureRate)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Gym Performance */}
          <div className="modern-card">
            <div className="modern-card-header">
              <h3 className="section-header">
                <Building2 size={20} className="text-[#1F4E79]" />
                Gym Performance ({filteredGymPerformance.length} gyms)
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Gym</th>
                    <th>Team Leader</th>
                    <th>Manager</th>
                    <th className="text-center">Dialed</th>
                    <th className="text-center">Connected</th>
                    <th className="text-center">Connect %</th>
                    <th className="text-center">Closures</th>
                    <th className="text-center">Avg/Day</th>
                    <th className="text-center">Gap</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGymPerformance.map((gym, idx) => {
                    const target = 30 * (data.daily_trend?.length || 1);
                    const gap = gym.closures - target;
                    let status = '✅';
                    if (gym.dialed === 0 || gym.connect_pct < 60) status = '🔴';
                    else if (gym.connect_pct < 70 || gap < 0) status = '🟡';
                    
                    return (
                      <tr key={idx} className={gym.dialed === 0 ? 'bg-red-50' : ''}>
                        <td>
                          <span className={`
                            inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold
                            ${idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}
                          `}>
                            {idx + 1}
                          </span>
                        </td>
                        <td className="font-semibold text-slate-800">{gym.gym}</td>
                        <td className="text-slate-600">{gym.tl_name || '-'}</td>
                        <td className="text-slate-600">{gym.manager_name || '-'}</td>
                        <td className="text-center">{gym.dialed}</td>
                        <td className="text-center">{gym.connected}</td>
                        <td className="text-center">
                          <span className="font-semibold" style={{ color: getStatusColor(gym.connect_pct) }}>
                            {gym.connect_pct}%
                          </span>
                        </td>
                        <td className="text-center font-semibold text-emerald-600">{gym.closures}</td>
                        <td className="text-center">{gym.avg_per_day}</td>
                        <td className="text-center">
                          <span className={`font-semibold ${gap >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {gap >= 0 ? '+' : ''}{gap}
                          </span>
                        </td>
                        <td className="text-center text-xl">{status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Where Did the Dip Happen */}
          <div className="modern-card border-red-200">
            <div className="modern-card-header bg-gradient-to-r from-red-50 to-white">
              <h3 className="section-header text-red-700">
                <AlertTriangle size={20} />
                Where Did The Dip Happen?
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="modern-table">
                <thead className="!bg-gradient-to-r !from-red-600 !to-red-700">
                  <tr>
                    <th>Gym</th>
                    <th>Team Leader</th>
                    <th>Metric</th>
                    <th className="text-center">Expected</th>
                    <th className="text-center">Actual</th>
                    <th className="text-center">Gap</th>
                    <th className="text-center">Severity</th>
                    <th className="text-center">Recurring?</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dips.length > 0 ? dips.map((dip, idx) => (
                    <tr key={idx} className={dip.severity === '🔴' ? 'bg-red-50' : ''}>
                      <td className="font-semibold text-slate-800">{dip.gym}</td>
                      <td className="text-slate-600">{dip.tl}</td>
                      <td>
                        <span className="status-badge info">{dip.metric}</span>
                      </td>
                      <td className="text-center text-slate-500">{dip.expected}</td>
                      <td className="text-center font-semibold text-red-600">{dip.actual}</td>
                      <td className="text-center font-semibold text-red-600">{dip.gap}</td>
                      <td className="text-center text-xl">{dip.severity}</td>
                      <td className="text-center">
                        <span className={`status-badge ${dip.recurring === 'Yes' ? 'error' : 'warning'}`}>
                          {dip.recurring}
                        </span>
                      </td>
                      <td>
                        <span className={`
                          px-3 py-1 rounded-md text-xs font-semibold
                          ${dip.action === 'Escalate' ? 'bg-red-600 text-white' : 
                            dip.action === 'Investigate FD' ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'}
                        `}>
                          {dip.action}
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="9" className="text-center py-8 text-emerald-600 font-medium">
                        ✅ No significant issues detected
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!data && !loading && (
        <div className="modern-card p-12 text-center">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="font-display text-2xl font-bold text-slate-700 mb-2">Ready to Analyze</h3>
          <p className="text-slate-500">Select a date range and click "Analyze" to view historical performance data</p>
        </div>
      )}
    </div>
  );
}
